// ./src/components/generic/PlanLockNote/PlanLockNote.tsx
//
// The per-CONTROL "this one is premium" line — a disabled switch or tile with a reason attached
// to it, rather than a disabled control the host has to guess about.
//
// ⚠ WHY NOT A TOOLTIP. Tooltips do not exist on touch devices. A `title=` attribute on a locked
// tile is invisible to every phone user, which is most hosts — so the reason has to be part of the
// layout, not a hover state. That is the whole reason this component exists.
//
// ⚠ WHY NOT `PlanFeatureNotice`. That is a full-width Alert, right for "this page cannot grow" at
// the top of a list. Putting one under each of six switches on Details & Settings would bury the
// form in banners. This is one quiet line, repeatable.
//
// ⚠ IT RENDERS NOTHING WHEN ALLOWED, so a call site can drop it in unconditionally next to the
// control it explains and not fork its own JSX.
import React from "react";
import { Anchor, Badge, Group, Text } from "@mantine/core";
import { IconLock } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { EntitlementVerdict } from "@/utils/planEntitlement";

export interface PlanLockNoteProps {
  /** The verdict from `useFeatureGate().checkFeature(...)`. Renders nothing when allowed. */
  verdict: EntitlementVerdict | null | undefined;
  /** Send the host somewhere that can fix it — normally `useFeatureGate().goToUpgrade`. */
  onUpgrade?: () => void;
  /**
   * Override the sentence. Defaults to the server's own refusal, so the host reads the same words
   * here as they would from a save.
   */
  reason?: string | null;
}

export const PlanLockNote: React.FC<PlanLockNoteProps> = ({ verdict, onUpgrade, reason }) => {
  const { t } = useTranslation(["common"]);

  if (!verdict || verdict.allowed) {
    return null;
  }

  const text = reason || verdict.message || t("common:plan.lockedShort", { defaultValue: "Not included in your current plan." });

  return (
    <Group gap="xs" align="center" wrap="wrap" mt={4}>
      <Badge
        color="yellow"
        variant="light"
        radius="sm"
        leftSection={<IconLock size={12} />}
      >
        {t("common:plan.premium", { defaultValue: "Premium" })}
      </Badge>

      <Text size="md" c="dimmed" style={{ flex: "1 1 200px" }}>
        {text}
        {onUpgrade ? (
          <>
            {" "}
            <Anchor component="button" type="button" onClick={onUpgrade} size="md">
              {t("common:plan.seePlans", { defaultValue: "See plans" })}
            </Anchor>
          </>
        ) : null}
      </Text>
    </Group>
  );
};
